import { useEffect, useMemo, useState } from 'react';
import { api } from '../api/client';
import { confirmDialog } from '../utils/confirm';
import { formatXof, PAYMENT_METHODS, INVOICE_STATUS } from '../utils/format';

const FEE_CATEGORIES = [
  'Frais de scolarité',
  'Frais de cantine/goûter',
  'Frais de tenue scolaire',
  'Frais de transport',
  "Frais de garde d'enfant",
  "Frais d'excursion",
  "Frais d'assurance scolaire",
  "Frais d'informatique",
  'Frais de bibliothèque',
  'Frais de bus scolaire',
  "Frais d'internat",
];

const OPERATION_TYPES = [
  'Paramétrage frais', 'Dépense', 'Frais divers', 'Paie',
  'Bulletin de paie', 'Avance sur salaire', 'Retenue sur salaire',
];

export default function Finance() {
  const [tab, setTab] = useState('recouvrement');
  return (
    <>
      <div className="tabs">
        <button className={`tab ${tab === 'recouvrement' ? 'active' : ''}`} onClick={() => setTab('recouvrement')}>Recouvrement</button>
        <button className={`tab ${tab === 'impayes' ? 'active' : ''}`} onClick={() => setTab('impayes')}>Impayés</button>
      </div>
      {tab === 'recouvrement' && <Recouvrement />}
      {tab === 'impayes' && <Impayes />}
    </>
  );
}

// ── Recouvrement ──────────────────────────────────────────
function groupByStudent(invoices) {
  const map = new Map();
  for (const inv of invoices) {
    const key = inv.invoiceNumber || inv.studentId;
    if (!map.has(key)) map.set(key, { key, invoiceNumber: inv.invoiceNumber || '—', student: inv.student, studentId: inv.studentId, lines: [] });
    map.get(key).lines.push(inv);
  }
  return Array.from(map.values()).map((g) => {
    const totalAmount  = g.lines.reduce((s, i) => s + i.amountXof, 0);
    const totalPaid    = g.lines.reduce((s, i) => s + (i.paid ?? 0), 0);
    const totalBalance = totalAmount - totalPaid;
    const statuses = new Set(g.lines.map((i) => i.status));
    let status = 'OUVERTE';
    if (statuses.size === 1 && statuses.has('PAYEE')) status = 'PAYEE';
    else if (statuses.has('PAYEE') || statuses.has('PARTIELLE')) status = 'PARTIELLE';
    else if (statuses.size === 1 && statuses.has('ANNULEE')) status = 'ANNULEE';
    return { ...g, totalAmount, totalPaid, totalBalance, status };
  });
}

function Recouvrement() {
  const [invoices, setInvoices]     = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');
  const [search, setSearch]         = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [expanded, setExpanded]     = useState(new Set());
  const [payModal, setPayModal]     = useState(null);
  const [receipt, setReceipt]       = useState(null);

  function load() {
    setLoading(true);
    const q = statusFilter ? `?status=${statusFilter}` : '';
    api.get(`/finance/invoices${q}`).then(setInvoices).catch((e) => setError(e.message)).finally(() => setLoading(false));
  }

  useEffect(() => { load(); }, [statusFilter]);

  const groups = useMemo(() => {
    const all = groupByStudent(invoices);
    const q = search.trim().toLowerCase();
    if (!q) return all;
    return all.filter((g) => `${g.student?.lastName ?? ''} ${g.student?.firstName ?? ''} ${g.student?.matricule ?? ''}`.toLowerCase().includes(q));
  }, [invoices, search]);

  const totalFacture  = invoices.reduce((s, i) => s + i.amountXof, 0);
  const totalEncaisse = invoices.reduce((s, i) => s + (i.paid ?? 0), 0);
  const totalRestant  = totalFacture - totalEncaisse;

  function toggleExpand(key) {
    setExpanded((prev) => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
  }

  return (
    <>
      <div className="kpi-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)', marginBottom: 16 }}>
        <div className="kpi navy"><span>Total facturé</span><strong>{formatXof(totalFacture)}</strong></div>
        <div className="kpi green"><span>Total encaissé</span><strong>{formatXof(totalEncaisse)}</strong></div>
        <div className="kpi gold"><span>Reste à recouvrer</span><strong>{formatXof(totalRestant)}</strong></div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <input className="search-input" placeholder="Élève, matricule…" value={search} onChange={(e) => setSearch(e.target.value)} style={{ flex: 1, maxWidth: 280 }} />
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          style={{ height: 36, border: '1px solid var(--line)', borderRadius: 6, padding: '0 10px', fontSize: 13, background: 'var(--surface)' }}>
          <option value="">Tous les statuts</option>
          <option value="OUVERTE">Ouvertes</option>
          <option value="PARTIELLE">Partielles</option>
          <option value="PAYEE">Payées</option>
          <option value="ANNULEE">Annulées</option>
        </select>
      </div>

      {error && <div className="form-error">{error}</div>}
      {receipt && <div className="receipt-banner">Paiement enregistré · reçu <b className="mono">{receipt.receiptNumber}</b> · {formatXof(receipt.amountXof)}</div>}

      {loading ? <div className="loading">Chargement…</div> : (
        <div className="card">
          <div className="table-wrap"><table>
            <thead>
              <tr>
                <th style={{ width: 24 }}></th>
                <th>Élève</th>
                <th>N° Facture</th>
                <th className="num">Montant</th>
                <th className="num">Encaissé</th>
                <th className="num">Solde</th>
                <th>Statut</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {groups.length === 0 && <tr><td colSpan="8" className="empty">Aucune facture.</td></tr>}
              {groups.map((g) => {
                const [badge, lbl] = INVOICE_STATUS[g.status] || ['gray', g.status];
                const isExp = expanded.has(g.key);
                return (
                  <>
                    <tr key={g.key} style={{ cursor: 'pointer' }} onClick={() => toggleExpand(g.key)}>
                      <td style={{ color: 'var(--muted)', fontSize: 11 }}>{isExp ? '▾' : '▸'}</td>
                      <td>
                        <div style={{ fontWeight: 600 }}>{g.student?.lastName} {g.student?.firstName}</div>
                        <small className="mono" style={{ color: 'var(--muted-light)', fontSize: 10 }}>{g.student?.matricule}</small>
                      </td>
                      <td className="mono">{g.invoiceNumber}</td>
                      <td className="num">{formatXof(g.totalAmount)}</td>
                      <td className="num">{formatXof(g.totalPaid)}</td>
                      <td className="num"><b>{formatXof(g.totalBalance)}</b></td>
                      <td><span className={`badge ${badge}`}>{lbl}</span></td>
                      <td style={{ textAlign: 'right' }}>
                        {g.totalBalance > 0 && (
                          <button className="btn btn-sm" onClick={(e) => { e.stopPropagation(); setPayModal(g); }}>Encaisser</button>
                        )}
                      </td>
                    </tr>
                    {isExp && g.lines.map((inv) => {
                      const [b2, l2] = INVOICE_STATUS[inv.status] || ['gray', inv.status];
                      return (
                        <tr key={inv.id} style={{ background: 'var(--surface-soft)', fontSize: 12 }}>
                          <td></td>
                          <td colSpan="2" style={{ paddingLeft: 28, color: 'var(--muted)' }}>{inv.label}{inv.category ? ` · ${inv.category}` : ''}</td>
                          <td className="num">{formatXof(inv.amountXof)}</td>
                          <td className="num">{formatXof(inv.paid ?? 0)}</td>
                          <td className="num">{formatXof(inv.amountXof - (inv.paid ?? 0))}</td>
                          <td><span className={`badge ${b2}`}>{l2}</span></td>
                          <td></td>
                        </tr>
                      );
                    })}
                  </>
                );
              })}
            </tbody>
          </table></div>
        </div>
      )}

      {payModal && (
        <PaymentModal
          student={payModal.student}
          invoices={payModal.lines.filter((i) => (i.amountXof - (i.paid ?? 0)) > 0)}
          onClose={() => setPayModal(null)}
          onDone={(pay) => { setPayModal(null); setReceipt(pay); load(); }}
        />
      )}
    </>
  );
}

function PaymentModal({ student, invoices, onClose, onDone }) {
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('ESPECES');
  const [invoiceId, setInvoiceId] = useState(invoices[0]?.id || '');
  const [reference, setReference] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  async function submit(e) {
    e.preventDefault(); setError(''); setSaving(true);
    try {
      const pay = await api.post('/finance/payments', {
        studentId: student.id,
        invoiceId: invoiceId || undefined,
        amountXof: Number(amount),
        method,
        reference: reference || undefined,
      });
      onDone(pay);
    } catch (err) { setError(err.message); setSaving(false); }
  }

  return (
    <div className="modal-overlay">
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>Encaisser un paiement</h2>
        <p style={{ color: 'var(--muted)', fontSize: '0.88rem', marginBottom: 16 }}>{student.lastName} {student.firstName}</p>
        {error && <div className="form-error">{error}</div>}
        <form onSubmit={submit} className="form-grid">
          <div className="field"><label>Montant (XOF)</label>
            <input type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Ex : 50000" required />
          </div>
          <div className="field"><label>Moyen de paiement</label>
            <select value={method} onChange={(e) => setMethod(e.target.value)}>
              {Object.entries(PAYMENT_METHODS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div className="field"><label>Imputer sur (facture)</label>
            <select value={invoiceId} onChange={(e) => setInvoiceId(e.target.value)}>
              <option value="">— Sans facture (avance) —</option>
              {invoices.map((i) => <option key={i.id} value={i.id}>{i.label} · solde {formatXof(i.balance)}</option>)}
            </select>
          </div>
          <div className="field"><label>Référence (optionnel)</label>
            <input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Ex : OM-123456" />
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
            <button className="btn" type="submit" disabled={saving} style={{ width: 'auto', padding: '0 22px' }}>{saving ? 'Enregistrement…' : 'Valider le paiement'}</button>
            <button type="button" className="btn-ghost" onClick={onClose}>Annuler</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Frais & facturation ───────────────────────────────────
function Frais() {
  const [fees, setFees] = useState([]);
  const [classes, setClasses] = useState([]);
  const [classFees, setClassFees] = useState({});
  const [error, setError] = useState('');
  const [savingMatrix, setSavingMatrix] = useState(false);
  const [savedMatrix, setSavedMatrix] = useState(false);
  const [form, setForm] = useState({ name: '', amountXof: '', category: '' });
  const [editFee, setEditFee] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', amountXof: '', category: '' });

  function loadFees() {
    api.get('/finance/fee-types').then(setFees).catch((e) => setError(e.message));
  }

  function loadClassFees() {
    api.get('/finance/class-fees').then((data) => {
      const map = {};
      (data || []).forEach(({ classId, feeTypeId }) => {
        if (!map[classId]) map[classId] = new Set();
        map[classId].add(feeTypeId);
      });
      setClassFees(map);
    }).catch(() => {});
  }

  useEffect(() => {
    loadFees();
    api.get('/catalog/classes').then(setClasses).catch(() => {});
    loadClassFees();
  }, []);

  function toggleFee(classId, feeTypeId) {
    setSavedMatrix(false);
    setClassFees((prev) => {
      const next = { ...prev };
      const s = new Set(next[classId] || []);
      if (s.has(feeTypeId)) s.delete(feeTypeId); else s.add(feeTypeId);
      next[classId] = s;
      return next;
    });
  }

  async function saveClassFees() {
    setSavingMatrix(true); setError('');
    try {
      const assignments = [];
      Object.entries(classFees).forEach(([classId, feeIds]) => {
        feeIds.forEach((feeTypeId) => assignments.push({ classId, feeTypeId }));
      });
      await api.put('/finance/class-fees', { assignments });
      setSavedMatrix(true);
    } catch (e) { setError(e.message); }
    setSavingMatrix(false);
  }

  async function addFee(e) {
    e.preventDefault(); setError('');
    try {
      await api.post('/finance/fee-types', { name: form.name, amountXof: Number(form.amountXof), category: form.category || undefined });
      setForm({ name: '', amountXof: '', category: '' });
      loadFees();
    } catch (err) { setError(err.message); }
  }

  async function updateFee(e) {
    e.preventDefault(); setError('');
    try {
      await api.put(`/finance/fee-types/${editFee.id}`, { name: editForm.name, amountXof: Number(editForm.amountXof), category: editForm.category || undefined });
      setEditFee(null);
      loadFees();
    } catch (err) { setError(err.message); }
  }

  async function deleteFee(id) {
    if (!confirm('Supprimer ce type de frais ?')) return;
    setError('');
    try {
      await api.delete(`/finance/fee-types/${id}`);
      loadFees();
    } catch (err) { setError(err.message); }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {error && <div className="form-error">{error}</div>}

      {/* Types de frais */}
      <div className="card">
        <div className="card-head"><h2>Types de frais</h2></div>
        <div className="card-body" style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 24 }}>
          <div className="table-wrap"><table>
            <thead><tr><th>Libellé</th><th>Catégorie</th><th className="num">Montant</th><th>Actions</th></tr></thead>
            <tbody>
              {fees.length === 0
                ? <tr><td colSpan="4" className="empty">Aucun frais défini.</td></tr>
                : fees.map((f) => (
                    <tr key={f.id}>
                      <td>{f.name}</td>
                      <td>{f.category || '—'}</td>
                      <td className="num">{formatXof(f.amountXof)}</td>
                      <td>
                        <button className="btn btn-sm" onClick={() => { setEditFee(f); setEditForm({ name: f.name, amountXof: f.amountXof, category: f.category || '' }); }} style={{ marginRight: 6 }}>✏️</button>
                        <button className="btn btn-sm btn-danger" onClick={() => deleteFee(f.id)}>🗑️</button>
                      </td>
                    </tr>
                  ))}
            </tbody>
          </table></div>

          <div>
            <h3 style={{ marginBottom: 12, fontSize: '0.9rem', fontWeight: 600 }}>Ajouter un type de frais</h3>
            <form onSubmit={addFee} className="form-grid">
              <div className="field"><label>Libellé *</label>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex : Frais d'inscription" required />
              </div>
              <div className="field"><label>Montant (XOF) *</label>
                <input type="number" min="0" value={form.amountXof} onChange={(e) => setForm({ ...form, amountXof: e.target.value })} required />
              </div>
              <div className="field"><label>Catégorie</label>
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                  <option value="">—</option>
                  {FEE_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
              <button className="btn" type="submit" style={{ width: 'auto', padding: '0 20px' }}>Ajouter</button>
            </form>
          </div>
        </div>
      </div>

      {/* Frais par classe */}
      <div className="card">
        <div className="card-head">
          <h2>Frais applicables par classe</h2>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            {savedMatrix && <span style={{ fontSize: '0.82rem', color: 'var(--success, green)' }}>✓ Enregistré</span>}
            <button className="btn btn-sm" onClick={saveClassFees} disabled={savingMatrix}>
              {savingMatrix ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </div>
        </div>
        <div className="card-body">
          {classes.length === 0 || fees.length === 0 ? (
            <div className="empty">Aucune classe ou aucun type de frais défini.</div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th style={{ minWidth: 120 }}>Classe</th>
                    {fees.map((f) => (
                      <th key={f.id} style={{ fontSize: 11, textAlign: 'center', maxWidth: 90, whiteSpace: 'normal', lineHeight: 1.3 }}>
                        {f.name}
                        <div style={{ fontWeight: 400, color: 'var(--muted)', fontSize: 10 }}>{formatXof(f.amountXof)}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {classes.map((cls) => (
                    <tr key={cls.id}>
                      <td><b>{cls.name}</b></td>
                      {fees.map((f) => (
                        <td key={f.id} style={{ textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={!!(classFees[cls.id]?.has(f.id))}
                            onChange={() => toggleFee(cls.id, f.id)}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal édition */}
      {editFee && (
        <div className="modal-overlay">
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head"><h3>Modifier le type de frais</h3><button onClick={() => setEditFee(null)}>✕</button></div>
            <div className="modal-body">
              <form onSubmit={updateFee} className="form-grid">
                <div className="field"><label>Libellé *</label>
                  <input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} required />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div className="field"><label>Montant (XOF) *</label>
                    <input type="number" min="0" value={editForm.amountXof} onChange={(e) => setEditForm({ ...editForm, amountXof: e.target.value })} required />
                  </div>
                  <div className="field"><label>Catégorie</label>
                    <select value={editForm.category} onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}>
                      <option value="">—</option>
                      {FEE_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                    </select>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button className="btn" type="submit">Enregistrer</button>
                  <button className="btn btn-secondary" type="button" onClick={() => setEditFee(null)}>Annuler</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Impayés ───────────────────────────────────────────────
function Impayes() {
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/finance/unpaid').then(setRows).catch((e) => setError(e.message)).finally(() => setLoading(false));
  }, []);

  const total = rows.reduce((s, r) => s + (r.balance || 0), 0);

  return (
    <div className="card">
      <div className="card-head">
        <h2>Élèves avec solde impayé</h2>
        <span className="badge red">{rows.length} élève(s) · {formatXof(total)}</span>
      </div>
      <div className="card-body">
        {error && <div className="form-error">{error}</div>}
        {loading ? <div className="loading">Chargement…</div> : rows.length === 0 ? (
          <div className="empty">Aucun impayé. ✓</div>
        ) : (
          <div className="table-wrap"><table>
            <thead>
              <tr><th>Élève</th><th>Classe</th><th>Matricule</th><th className="num">Total dû</th><th className="num">Payé</th><th className="num">Solde</th></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.studentId}>
                  <td><b>{r.lastName} {r.firstName}</b></td>
                  <td>{r.className || '—'}</td>
                  <td className="mono">{r.matricule}</td>
                  <td className="num">{formatXof(r.totalDu)}</td>
                  <td className="num">{formatXof(r.totalPaye)}</td>
                  <td className="num"><b style={{ color: 'var(--danger)' }}>{formatXof(r.balance)}</b></td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </div>
    </div>
  );
}

// ── Dépenses & paie ───────────────────────────────────────
function Depenses() {
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const [modal, setModal] = useState(null);
  const [staff, setStaff] = useState([]);

  function load() {
    api.get('/finance/payments?type=DEPENSE').then(setRows).catch(() => {});
  }

  useEffect(() => {
    load();
    api.get('/staff').then(setStaff).catch(() => {});
  }, []);

  const totalDepenses = rows.reduce((s, r) => s + (r.amountXof || 0), 0);

  return (
    <div className="card">
      <div className="card-head">
        <h2>Dépenses & paie du personnel</h2>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <span className="badge navy">{formatXof(totalDepenses)} total</span>
          <button className="btn btn-sm" onClick={() => setModal({})}>+ Nouvelle dépense</button>
        </div>
      </div>
      <div className="card-body">
        {error && <div className="form-error">{error}</div>}
        {rows.length === 0 ? <div className="empty">Aucune dépense enregistrée.</div> : (
          <div className="table-wrap"><table>
            <thead>
              <tr><th>Type</th><th>Agent</th><th>Catégorie</th><th>Date</th><th>Moyen</th><th className="num">Montant</th><th>Réf.</th></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td>{r.operationType || '—'}</td>
                  <td>{r.staffName || '—'}</td>
                  <td>{r.category || '—'}</td>
                  <td className="mono">{r.receivedAt ? new Date(r.receivedAt).toLocaleDateString('fr-FR') : '—'}</td>
                  <td>{PAYMENT_METHODS[r.method] || r.method || '—'}</td>
                  <td className="num"><b>{formatXof(r.amountXof)}</b></td>
                  <td className="mono">{r.receiptNumber || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </div>
      {modal && <DepenseModal staff={staff} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
    </div>
  );
}

function DepenseModal({ staff, onClose, onSaved }) {
  const [f, setF] = useState({ operationType: 'Paie', staffId: '', category: 'Salaire', amountXof: '', method: 'VIREMENT', reference: '', payrollPeriod: '', notes: '' });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  async function save() {
    if (!f.amountXof) { setError('Le montant est obligatoire.'); return; }
    setSaving(true);
    try {
      await api.post('/finance/payments', {
        amountXof: Number(f.amountXof),
        method: f.method,
        reference: f.reference || undefined,
        notes: f.notes || undefined,
        staffId: f.staffId || undefined,
        operationType: f.operationType,
        category: f.category,
        payrollPeriod: f.payrollPeriod || undefined,
      });
      onSaved();
    } catch (e) { setError(e.message); setSaving(false); }
  }

  return (
    <div className="modal-overlay">
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>Nouvelle dépense / paie</h2>
        {error && <div className="form-error">{error}</div>}
        <div className="field"><label>Type d'opération</label>
          <select value={f.operationType} onChange={set('operationType')}>
            {OPERATION_TYPES.map((t) => <option key={t}>{t}</option>)}
          </select>
        </div>
        <div className="field"><label>Agent concerné</label>
          <select value={f.staffId} onChange={set('staffId')}>
            <option value="">— Général —</option>
            {staff.map((s) => <option key={s.id} value={s.id}>{s.lastName} {s.firstName}</option>)}
          </select>
        </div>
        <div className="field"><label>Catégorie</label>
          <select value={f.category} onChange={set('category')}>
            <option value="Salaire">Salaire</option>
            <option value="Dépense fonctionnement">Dépense fonctionnement</option>
            <option value="Avance">Avance sur salaire</option>
            <option value="Autre">Autre</option>
          </select>
        </div>
        {['Paie', 'Bulletin de paie', 'Avance sur salaire'].includes(f.operationType) && (
          <div className="field"><label>Période de paie</label>
            <input value={f.payrollPeriod} onChange={set('payrollPeriod')} placeholder="Ex : Août 2026" />
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className="field"><label>Montant (XOF) *</label>
            <input type="number" min="0" value={f.amountXof} onChange={set('amountXof')} />
          </div>
          <div className="field"><label>Moyen de paiement</label>
            <select value={f.method} onChange={set('method')}>
              {Object.entries(PAYMENT_METHODS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
        </div>
        <div className="field"><label>Référence</label>
          <input value={f.reference} onChange={set('reference')} placeholder="N° de pièce ou reçu" />
        </div>
        <div className="field"><label>Notes</label>
          <textarea value={f.notes} onChange={set('notes')} rows={2} />
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
          <button className="btn" onClick={save} disabled={saving} style={{ width: 'auto', padding: '0 22px' }}>
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </button>
          <button className="btn-ghost" onClick={onClose}>Annuler</button>
        </div>
      </div>
    </div>
  );
}
